import React, { useCallback, useRef, useState } from 'react';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

interface Props {
    onFileSelect: (files: FileList) => void;
}

const FileDropZone = (props: React.PropsWithChildren<Props>) => {
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrop = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            setDragActive(false);

            const files = event.dataTransfer.files;
            if (files?.length > 0) {
                props.onFileSelect(files);
            }
        },
        [props.onFileSelect]
    );

    const handleDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
        setDragActive(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setDragActive(false);
    }, []);

    const handleFileInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            if (files?.length > 0) {
                props.onFileSelect(files);
            }
        },
        [props.onFileSelect]
    );

    const handleDropZoneClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleButtonClick = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
        fileInputRef.current?.click();
    }, []);

    return (
        <StyledDropZone
            dragActive={dragActive}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleDropZoneClick}
        >
            <Typography variant="body1" color="textSecondary" mb={0.5}>
                Drag and drop G1000 files here
            </Typography>
            <Typography variant="body1" color="textSecondary" mb={0.5}>
                or
            </Typography>
            <Button
                variant="contained"
                sx={{ backgroundColor: 'grey' }}
                startIcon={<CloudUploadIcon />}
                onClick={handleButtonClick}
            >
                Browse Files
            </Button>
            <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                onChange={handleFileInputChange}
                multiple={false}
            />
        </StyledDropZone>
    );
};

const StyledDropZone = styled(Paper, {
    shouldForwardProp: prop => prop !== 'dragActive',
})<{ dragActive: boolean }>(({ theme, dragActive }) => ({
    border: `2px dashed ${dragActive ? theme.palette.primary.main : theme.palette.grey[400]}`,
    backgroundColor: dragActive ? '#f0f0f0' : '#fafafa',
    padding: theme.spacing(4),
    textAlign: 'center',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    outline: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
        borderColor: theme.palette.grey[500],
        '& .MuiTypography-root': {
            color: theme.palette.grey[600],
        },
        '& .MuiButton-root': {
            backgroundColor: theme.palette.grey[500],
            color: theme.palette.grey[100],
        },
        '& .MuiSvgIcon-root': {
            color: theme.palette.grey[600],
        },
    },
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

export default FileDropZone;
